import { AppConfig, Config, ConfigService } from '@services/config.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { LocationSelectorService } from '@services/location_selector.service';
import { Scoped } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { UnitsApiService } from '@src/api/units_api.service';
import type { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import type { Language } from '@components/language_select/language_select.component';
import type { UnitAboutApiModel } from '@api/generated/models';

export interface AddressField {
	key: string;
	value: string;
}

export interface LegalField {
	key: string;
	translationKey: I18nAndL10n.LocaleKey;
	value: string;
}

export interface ExternalLink {
	name: string | null;
	url: string;
}

interface Presentation {
	aboutUsText: string | null;
	addressInfo: AddressField[];
	legal: LegalField[];
	languages: Language[];
	externalLink: ExternalLink | null;
	appConfig: AppConfig | null;
}

type FetchedTexts = 'Web_About';

@Scoped()
export class FooterController {
	public readonly presentation = Presentation.createConcurrent<Presentation>();

	constructor(
		private readonly unitsApiService: UnitsApiService,
		private readonly locationSelectorService: LocationSelectorService,
		private readonly licenceHolderService: LicenceHolderService,
		private readonly configService: ConfigService,
	) {
		// Empty
	}

	public onMount(): void {
		const unitAboutPromise = this.unitsApiService.getLicenseHolderAboutData(this.licenceHolderService.rootUnitId);
		const textsPromise = this.unitsApiService.getTexts(this.licenceHolderService.rootUnitId, ['Web_About']);
		const unitConfig = this.configService.loadConfig(this.locationSelectorService.locationUnitId);

		const promiseGroup = Promise.all([unitAboutPromise, unitConfig, textsPromise]);
		this.presentation.suspend(promiseGroup, ([unitAboutResponse, unitConfig, textsPromise]) => {
			return this.parseResultsToPresentationModel([unitAboutResponse.data, unitConfig, textsPromise.data]);
		});
	}

	private parseResultsToPresentationModel([unitAbout, unitConfig, webAbout]: readonly [
		UnitAboutApiModel,
		Config,
		Awaited<ReturnType<typeof this.unitsApiService.getTexts<FetchedTexts>>>['data'],
	]): Presentation {
		const addressInfo: AddressField[] = [];
		if (unitAbout.VisitAddress?.Address != null) {
			addressInfo.push({
				key: 'Address+Address2',
				value: [unitAbout.VisitAddress.Address, unitAbout.VisitAddress.Address2].filter(Boolean).join(', '),
			});
		}
		if (unitAbout.VisitAddress?.City != null) {
			addressInfo.push({
				key: 'PostalCode+City',
				value: [unitAbout.VisitAddress.PostalCode, unitAbout.VisitAddress.City].filter(Boolean).join(' '),
			});
		}

		const legal: LegalField[] = [];
		if (unitConfig.privacyPolicyUrl != null) {
			legal.push({
				key: 'pp',
				value: unitConfig.privacyPolicyUrl,
				translationKey: 'PW.Component.Footer.PP',
			});
		}
		if (unitConfig.termsOfServiceUrl != null) {
			legal.push({
				key: 'tos',
				value: unitConfig.termsOfServiceUrl,
				translationKey: 'PW.Component.Footer.ToS',
			});
		}
		legal.push({
			key: 'Cookie-Policy',
			value: `/${this.licenceHolderService.alias}/cookie-policy`,
			translationKey: 'PW.Component.Footer.CookiePolicy',
		});

		return {
			legal,
			addressInfo,
			aboutUsText: webAbout.Web_About,
			languages: unitConfig.languages.map((language) => ({
				code: language.code,
				key: language.code,
				nativeName: language.nativeName,
			})),
			externalLink:
				unitAbout.Url != null
					? {
							name: unitAbout.UrlLinkName,
							url: unitAbout.Url,
						}
					: null,
			appConfig: unitConfig.appConfig,
		};
	}
}
